import {BASE_URL} from "@/api/base-api";
import {ErrorToast, SuccessToast} from "@/components/VueToast";
import moment from "moment";
import {piniaUserStore} from "@/store/piniaUserStore";
import {piniaEnumsStore} from "@/store/piniaEnumsStore";
import {ICoachingOverview} from "@/interfaces/CoachingOverview/AssistedLivingCoaching/ICoachingOverview";

export const TENANT_KEY = process.env.VUE_APP_TENANT_KEY

export async function initAppIfAuthenticated() {
    const token = localStorage.getItem('token')
    if (!token) return false

    const userStore = piniaUserStore()
    const enumStore = piniaEnumsStore()

    await Promise.all([
        userStore.piniaGetUser(),
        enumStore.getEnums(),
    ])

    return true
}


export const getSortIconClass = (filters: any, column: string) => {
    return [
        'os-icon',
        filters.sortBy === column
            ? filters.orderBy === 'DESC'
                ? 'os-icon-arrow-down6'
                : 'os-icon-arrow-up6'
            : 'os-icon-arrow-down6',
        'font-weight-bolder'
    ];
};

export function isColorDark(hexColor: string) {
    hexColor = hexColor.replace("#", "");

    const r = parseInt(hexColor.substring(0, 2), 16);
    const g = parseInt(hexColor.substring(2, 4), 16);
    const b = parseInt(hexColor.substring(4, 6), 16);

    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

    return luminance < 128;
}

export function hexToRgba(hex: string, opacity: number) {
    hex = hex.replace(/^#/, '');

    if (hex.length === 3) {
        hex = hex.split('').map(char => char + char).join('');
    }

    const bigint = parseInt(hex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export function calculateTimeDifference(startTime: string, endTime: string, pauseDuration: string) {
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);
    const [pauseHours, pauseMinutes] = pauseDuration ? pauseDuration.split(':').map(Number) : [0, 0];

    const startTotalMinutes = startHours * 60 + startMinutes;
    const endTotalMinutes = endHours * 60 + endMinutes;
    const pauseTotalMinutes = pauseHours * 60 + pauseMinutes;

    const differenceInMinutes = endTotalMinutes - startTotalMinutes - pauseTotalMinutes;

    const diffHours = Math.floor(differenceInMinutes / 60);
    const diffMinutes = Math.abs(differenceInMinutes % 60);

    return `${String(diffHours).padStart(2, '0')}:${String(diffMinutes).padStart(2, '0')}`;
}


export function calculateOvertime(planedHoursTime: string, startTime: string, endTime: string, pauseDuration: string) {
    const [planedHours, planedMinutes] = planedHoursTime.split(':').map(Number);
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);
    const [pauseHours, pauseMinutes] = pauseDuration.split(':').map(Number);

    const planedHoursTotalMinutes = planedHours * 60 + planedMinutes;
    const startTotalMinutes = startHours * 60 + startMinutes;
    const endTotalMinutes = endHours * 60 + endMinutes;
    const pauseTotalMinutes = pauseHours * 60 + pauseMinutes;

    // const differenceInMinutes = (endTotalMinutes - startTotalMinutes - pauseTotalMinutes) - planedHoursTotalMinutes;
    // const diffHours = Math.floor(differenceInMinutes / 60);
    // const diffMinutes = Math.abs(differenceInMinutes % 60);ca

    const differenceInMinutes = (endTotalMinutes - startTotalMinutes - pauseTotalMinutes) - planedHoursTotalMinutes;

    const sign = differenceInMinutes < 0 ? "-" : "";
    const totalMinutes = Math.abs(differenceInMinutes);

    const diffHours = Math.floor(totalMinutes / 60);
    const diffMinutes = totalMinutes % 60;

    return `${sign}${String(diffHours).padStart(2, '0')}:${String(diffMinutes).padStart(2, '0')}`;
}


export function truncateText(text: string, maxLength: number, suffix: string = '...'): string {
    if (text.length <= maxLength) {
        return text;
    }
    return text.slice(0, maxLength) + suffix;
}

export const formatDate = (date: Date | string | undefined, format?: string) => {
    return moment(date)?.format(format ?? 'DD.MM.YYYY');
};

export const downloadFile = async (
    url: string,
    originalName: string | undefined,
    filterParams?: any,
    preview = true,
    method = "GET",
    isLoaded?: any,
) => {

    if (isLoaded) {
        isLoaded.value = true;
    }

    const fullUrl = BASE_URL + url;

    let downloadUrl;
    if (method == 'GET') {
        const params = new URLSearchParams(filterParams).toString();
        downloadUrl = params ? `${fullUrl}?${params}` : fullUrl;
    } else {
        downloadUrl = fullUrl;
    }

    const token = localStorage.getItem('token') ?? '{}';
    if (!token) {
        await ErrorToast('Authorization token not found');
        return;
    }


    const appVersion = localStorage.getItem('appVersion');
    const xhr = new XMLHttpRequest();
    xhr.open(method, downloadUrl, true);
    xhr.responseType = 'blob';
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader("Content-Type", "application/json");
    xhr.setRequestHeader("app-version", appVersion ?? "unknown"); // ✅ extra header
    xhr.setRequestHeader("tenant-key", TENANT_KEY ?? ""); // ✅ extra header

    xhr.onload = () => {
        if (xhr.status === 200) {
            const blob = new Blob([xhr.response], {type: xhr.getResponseHeader('Content-Type') || 'application/octet-stream'});
            const url = URL.createObjectURL(blob);

            const contentType = xhr.getResponseHeader('Content-Type')!;
            const contentDisposition = xhr.getResponseHeader('Content-Disposition') || xhr.getResponseHeader('content-disposition');

            // if (contentDisposition && contentDisposition.indexOf('attachment') !== -1) {
            //     const regex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            //     const matches = regex.exec(contentDisposition);
            //     if (matches != null && matches[1]) {
            //         aaaaaaa = matches[1].replace(/['"]/g, '');
            //     }
            // }

            let fileExtension = '';

            if (contentType.includes('application/pdf')) {
                fileExtension = 'pdf';
            } else if (contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
                fileExtension = 'xlsx';
            } else if (contentType.includes('application/vnd.openxmlformats-officedocument.wordprocessingml.document')) {
                fileExtension = 'docx';
            } else if (contentType.includes('application/vnd.ms-powerpoint')) {
                fileExtension = 'ppt';
            } else if (contentType.includes('application/vnd.openxmlformats-officedocument.presentationml.presentation')) {
                fileExtension = 'pptx';
            } else if (contentType.includes('text/plain')) {
                fileExtension = 'txt';
            } else if (contentType.includes('image/jpeg') || contentType.includes('image/jpg')) {
                fileExtension = 'jpg';
            } else if (contentType.includes('image/png')) {
                fileExtension = 'png';
            } else if (contentType.includes('application/zip')) {
                fileExtension = 'zip';
            } else {
                fileExtension = 'bin';
            }

            let fileName = originalName ? `${originalName}.${fileExtension}` : `${fullUrl.split('/').at(-1)}.${fileExtension}`;

            if (contentDisposition && contentDisposition.indexOf('attachment') !== -1) {
                const regex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = regex.exec(contentDisposition);
                if (matches != null && matches[1]) {
                    fileName = matches[1].replace(/['"]/g, '');
                }
            }

            // Decode the filename to handle special characters
            fileName = decodeURIComponent(escape(fileName)); // Decode special characters

            if (preview) {
                const blobUrl = URL.createObjectURL(blob);
                const safeFilename = encodeURIComponent(fileName);
                const printWindow = window.open(`${blobUrl}`, '_blank');


                if (isSafari()) {
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = fileName;
                    document.body.appendChild(a);
                    a.click();

                    setTimeout(() => {
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                    }, 0);

                    SuccessToast("Dokument heruntergeladen");

                } else {
                    if (printWindow) {
                        printWindow.onload = () => {
                            printWindow.focus();
                        };
                    } else {
                        ErrorToast('Popup blocked! Please allow popups for this site.');
                    }
                }



                // const link = document.createElement('a');
                // link.href = blobUrl;
                // link.target = "_blank";
                // link.rel = "noopener noreferrer";
                // link.click();
                // // document.body.removeChild(link);
                // URL.revokeObjectURL(blobUrl);
            } else {
                const a = document.createElement('a');
                a.href = url;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();

                setTimeout(() => {
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                }, 0);

                SuccessToast("Dokument heruntergeladen");
            }

            if (isLoaded) {
                isLoaded.value = false;
            }

        } else {
            if (isLoaded) {
                isLoaded.value = false;
            }
            ErrorToast(`Download failed: ${xhr.statusText}`);
        }
    };

    xhr.onerror = () => {
        if (isLoaded) {
            isLoaded.value = false;
        }
        ErrorToast('Network error occurred while trying to download the file.');
    };

    if (method == 'POST') {
        xhr.send(JSON.stringify(filterParams))
    } else {
        xhr.send();
    }

};

export const formatRole = (role: string): string => {
    return role.replace(/^ROLE_/, "").toLowerCase();
};

// Function to decode file name properly
function decodeFileName(name: string): string {
    try {
        // Use TextDecoder to decode a possibly misinterpreted file name
        const decoder = new TextDecoder('utf-8');
        const encodedFileName = new TextEncoder().encode(name);
        return decoder.decode(encodedFileName);
    } catch (error) {
        // If decoding fails, fallback to the original name
        return name;
    }
}


export const getFileType = (fileName: string, path?: string, isLink?: string) => {
    const extension = (fileName?.split('.').pop() || '').toLowerCase();
    // if (isLink) return {type: 'link', imgSrc: '/tempAssets/videoPlaceholder.svg' };
    switch (extension) {
        case 'pdf':
            return {type: 'pdf', iconClass: 'fi fi-rr-file-pdf text-danger', imgSrc: '/icons/file-pdf.svg'};
        case 'doc':
        case 'docx':
            return {type: 'word', iconClass: 'fi fi-rr-file-word text-primary', imgSrc: '/icons/file-word.svg'};
        case 'xls':
        case 'xlsx':
            return {type: 'excel', iconClass: 'fi fi-rr-file-excel text-success', imgSrc: '/icons/file-excel.svg'};
        case 'ppt':
        case 'pptx':
            return {
                type: 'powerpoint',
                iconClass: 'fi fi-rr-file-powerpoint text-warning',
                imgSrc: '../assets/icons/file-powerpoint.svg'
            };
        case 'jpg':
        case 'png':
        case 'jpeg':
            return {type: 'image', iconClass: 'fi fi-rr-picture text-muted', imgSrc: path};
        case 'mp4':
            return {type: 'video', iconClass: 'fi fi-rr-file-video text-muted', imgSrc: path};
        case 'm4a':
        case 'mp3':
            return {type: 'audio', iconClass: 'fi fi-rr-file-audio text-muted', imgSrc: '../assets/file-audio.svg'};
        default:
            return {type: 'other', iconClass: 'fi fi-rr-file text-muted', imgSrc: '../assets/file.svg'};
    }
};


function isSafari() {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}


export function getUnitsInWeek(coaching: ICoachingOverview): number | null {
    if (!coaching.calculatedUnitsInWeek?.length) {
        return null;
    }

    const today = new Date();

    const parseDate = (value?: string): Date | null => {
        if (!value) return null;

        const [y, m, d] = value.split('.').map(Number);
        return new Date(y, m - 1, d);
    };

    // 1. First try: find the current week based on startDate and endDate
    const current = coaching.calculatedUnitsInWeek.find(item => {
        const start = parseDate(item.startDate);
        const end = parseDate(item.endDate);

        if (!start || !end) return false;

        return today >= start && today <= end;
    });

    if (current) {
        return current.unitsInWeek ?? null;
    }

    // 2. fallback: find the last entry with a valid endDate and return its unitsInWeek
    const last = [...coaching.calculatedUnitsInWeek]
        .filter(i => i.endDate)
        .sort((a, b) => {
            const aDate = parseDate(a.endDate!)!;
            const bDate = parseDate(b.endDate!)!;
            return aDate.getTime() - bDate.getTime();
        })
        .at(-1);

    return last?.unitsInWeek ?? null;
}